<template>
    <p-modal
        name="dailySalesReportModal"
        label="Daily Sales Report"
        ref="dailySalesReportModal"
        size="lg"
        @ok.prevent="handleModalOk">
        <template v-slot:default>
            <p-form ref="dailySalesReportForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Tender, DCC, hourly, discount and item detail by POS device</p>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="POS Devices" class="static-height">
                                <p-filter-select
                                    data-type="posDevicesWithLocation"
                                    rules="required"
                                    :default-selected="true"
                                    :initial-display-count="10"
                                    v-model="reportParameters.registerIds" />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                            <p-card title="Tender Status">
                                <b-form-radio :value="true" v-model="reportParameters.tenderStatusOption">Active Tenders Only</b-form-radio>
                                <b-form-radio :value="false" v-model="reportParameters.tenderStatusOption" class="pt-1">All</b-form-radio>
                            </p-card>
                            <p-card title="Sections">
                                <p-checkbox
                                    size="md"
                                    label="POS Device Reconciliation"
                                    v-model="reportParameters.includeReconciliation" />
                                <p-checkbox
                                    size="md"
                                    label="Sales by DCC"
                                    v-model="reportParameters.includeSalesByDcc" />
                                <p-checkbox
                                    size="md"
                                    label="Transaction Volume by Tender"
                                    v-model="reportParameters.includeTenderVolume" />
                                <p-checkbox
                                    size="md"
                                    label="Transactions by Hour by Tender"
                                    v-model="reportParameters.includeHourlyTender" />
                                <p-checkbox
                                    size="md"
                                    label="Discounts by DCC"
                                    v-model="reportParameters.includeDiscountsByDcc" />
                                <p-checkbox
                                    size="md"
                                    label="Product Movement by Item"
                                    v-model="reportParameters.includeProductMovement" />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button variant="outline-primary" :is-busy="isBusy" @click="cancel">Cancel</p-button>
                    <p-button variant="primary" :is-busy="isBusy" class="ms-2" @click="ok">Export</p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import pCard from '@/components/p-card.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

// One flag per report section, in report order. All are on by default and at least one is required.
const sectionFlags = [
    'includeReconciliation',
    'includeSalesByDcc',
    'includeTenderVolume',
    'includeHourlyTender',
    'includeDiscountsByDcc',
    'includeProductMovement'
];

export default {
    mixins: [ReportTemplates],
    components: { pCard, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Daily Sales Report',
            reportParameters: {
                registerIds: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                tenderStatusOption: true,
                locationId: null,
                includeReconciliation: true,
                includeSalesByDcc: true,
                includeTenderVolume: true,
                includeHourlyTender: true,
                includeDiscountsByDcc: true,
                includeProductMovement: true
            }
        };
    },
    methods: {
        async show() {
            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            this.reportParameters.registerIds = (posDevices || []).map(x => x.value);
            sectionFlags.forEach(flag => {
                this.reportParameters[flag] = true;
            });
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.dailySalesReportModal.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be selected, and the
        // mixin warns and loads nothing. The POS device ids are the only saved values tied to changing data.
        async validateReportParameters(savedParameters) {
            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation') ?? [];

            return (savedParameters.registerIds ?? []).every(registerId =>
                posDevices.some(posDevice => posDevice.value == registerId));
        },
        async handleModalOk() {
            if (!(await this.$refs.dailySalesReportForm.validate())) {
                return;
            }

            if (this.reportParameters.registerIds.length === 0) {
                this.$appError('Please select one or more Pos Devices.');
                return;
            }

            if (!sectionFlags.some(flag => this.reportParameters[flag])) {
                this.$appError('Please select at least one section.');
                return;
            }

            this.isBusy = true;

            try {
                // The current location always wins: a restored template carries the location that was active when it
                // was saved, which would otherwise run the report for the wrong one.
                this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

                if (!(await this.saveUserReportTemplate())) {
                    return;
                }

                const pdf = await axios.post('/sales/exportDailySalesReport', this.reportParameters, { responseType: 'blob' });

                downloadFileToBrowser(pdf.data, 'Daily Sales Report.pdf', 'application/pdf');
                this.$appSuccess('Daily Sales Report generated successfully.');
                this.$refs.dailySalesReportModal.hide();
            } finally {
                this.isBusy = false;
            }
        }
    }
};
</script>

<style scoped>
.static-height {
    height: 370px;
}
</style>
